import { useCallback } from 'react';
import { message } from 'antd';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import { useCrmStore } from '../stores/crm-store';
import { CrmInteractionDTO, PageResponse } from '../types';

const API_BASE = '/api/crm/interactions';

export const useInteractions = () => {
    const {
        interactions, selectedInteraction, interactionTotal,
        isLoading, isSubmitting, error,
        setInteractions, setSelectedInteraction, setLoading, setSubmitting, setError,
    } = useCrmStore();

    const search = useCallback(async (params: {
        custNo?: string;
        dateFrom?: string;
        dateTo?: string;
        page?: number;
        size?: number;
    } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await AXIOS_INSTANCE.get<PageResponse<CrmInteractionDTO>>(API_BASE, { params });
            setInteractions(response.data.content, response.data.totalElements);
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to fetch interactions';
            setError(msg);
            message.error(msg);
        } finally {
            setLoading(false);
        }
    }, [setLoading, setError, setInteractions]);

    const fetchByCustomer = useCallback(async (branch: string, custNo: string): Promise<CrmInteractionDTO[]> => {
        try {
            const response = await AXIOS_INSTANCE.get<CrmInteractionDTO[]>(
                `${API_BASE}/customer/${branch}/${custNo}`
            );
            return response.data;
        } catch {
            return [];
        }
    }, []);

    const create = useCallback(async (data: CrmInteractionDTO): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.post<CrmInteractionDTO>(API_BASE, data);
            message.success('Interaction logged successfully');
            return true;
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to log interaction';
            setError(msg);
            message.error(msg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError]);

    const update = useCallback(async (id: number, data: CrmInteractionDTO): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.put<CrmInteractionDTO>(`${API_BASE}/${id}`, data);
            message.success('Interaction updated');
            return true;
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to update interaction';
            setError(msg);
            message.error(msg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError]);

    const remove = useCallback(async (id: number): Promise<boolean> => {
        setSubmitting(true);
        try {
            await AXIOS_INSTANCE.delete(`${API_BASE}/${id}`);
            message.success('Interaction deleted');
            return true;
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Failed to delete interaction');
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting]);

    return {
        interactions, selectedInteraction, interactionTotal,
        isLoading, isSubmitting, error,
        setSelectedInteraction,
        search, fetchByCustomer, create, update, remove,
    };
};
